import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Loader2, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { toast } from 'sonner';

const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { t } = useLanguage();
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await login(formData.email, formData.password);
      toast.success(t.auth.welcomeBack.split('!')[0] + '!');
      navigate('/');
    } catch (error) {
      console.error('Login error:', error);
      toast.error(error.response?.data?.detail || t.common.error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-4" data-testid="login-page">
      <div className="w-full max-w-md">
        <div className="border border-zinc-800 bg-[#111111] p-8">
          <div className="text-center mb-8">
            <Link to="/" className="inline-block">
              <span className="font-heading text-3xl text-yellow-400 uppercase tracking-tight">Narzędzia Pro</span>
            </Link>
            <h1 className="font-heading text-2xl uppercase text-white mt-6" data-testid="login-title">{t.auth.login}</h1>
            <p className="text-zinc-400 text-sm mt-2">{t.auth.welcomeBack}</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="font-mono text-xs tracking-widest uppercase text-zinc-400 mb-2 block">{t.auth.email}</label>
              <input type="email" name="email" value={formData.email} onChange={handleChange} required className="bg-[#050505] border border-zinc-700 text-white px-4 py-3 font-sans focus:border-yellow-400 focus:outline-none focus:ring-1 focus:ring-yellow-400 transition-all w-full" placeholder="your@email.com" data-testid="login-email" />
            </div>

            <div>
              <label className="font-mono text-xs tracking-widest uppercase text-zinc-400 mb-2 block">{t.auth.password}</label>
              <div className="relative">
                <input type={showPassword ? 'text' : 'password'} name="password" value={formData.password} onChange={handleChange} required className="bg-[#050505] border border-zinc-700 text-white px-4 py-3 pr-12 font-sans focus:border-yellow-400 focus:outline-none focus:ring-1 focus:ring-yellow-400 transition-all w-full" placeholder="••••••••" data-testid="login-password" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-4 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white">
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <button type="submit" disabled={loading} className="w-full bg-yellow-400 text-black uppercase font-bold tracking-widest px-8 py-4 border-2 border-yellow-400 hover:bg-black hover:text-yellow-400 transition-colors flex items-center justify-center gap-2 disabled:opacity-50" data-testid="login-submit">
              {loading ? (<><Loader2 size={20} className="animate-spin" /> {t.auth.loggingIn}</>) : t.auth.login}
            </button>
          </form>

          <p className="text-center text-zinc-400 text-sm mt-6">
            {t.auth.noAccount}{' '}
            <Link to="/register" className="text-yellow-400 hover:underline" data-testid="register-link">{t.auth.register}</Link>
          </p>

          <div className="mt-8 pt-6 border-t border-zinc-800">
            <p className="text-center text-zinc-500 text-xs font-mono mb-2">{t.auth.demoCredentials}</p>
            <p className="text-center text-zinc-400 text-xs font-mono">admin@pronarzedzia.pl / admin123</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
